import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionHead, Reveal } from "./Reveal";
import { GAP_QUESTIONS, GAP_REVIEW_ITEMS } from "@/data/content";
import { ShieldAlert, ShieldCheck, ShieldHalf, RotateCcw, ArrowRight } from "lucide-react";

const RESULTS = {
    low: { icon: ShieldCheck, color: "text-emerald-400", border: "border-emerald-400/30", bg: "bg-emerald-400/5", title: "Low Risk", msg: "Your compliance foundation looks structured. Periodic reviews will keep it that way — especially with the new Labour Codes on the horizon." },
    moderate: { icon: ShieldHalf, color: "text-amber-400", border: "border-amber-400/30", bg: "bg-amber-400/5", title: "Moderate Risk", msg: "Some compliance areas need attention. A structured review of registrations, registers and due dates is recommended before gaps become liabilities." },
    high: { icon: ShieldAlert, color: "text-red-400", border: "border-red-400/30", bg: "bg-red-400/5", title: "High Risk", msg: "Significant compliance gaps are likely. We recommend an immediate compliance review covering registrations, registers, returns and contractor compliance." },
};

const GapAssessment = () => {
    const [step, setStep] = useState(0);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);

    const answer = (s) => {
        const next = score + s;
        if (step === GAP_QUESTIONS.length - 1) {
            setScore(next);
            setDone(true);
        } else {
            setScore(next);
            setStep(step + 1);
        }
    };

    const reset = () => {
        setStep(0);
        setScore(0);
        setDone(false);
    };

    const result = score <= 2 ? RESULTS.low : score <= 5 ? RESULTS.moderate : RESULTS.high;
    const ResultIcon = result.icon;

    return (
        <section id="gap-assessment" className="py-24 sm:py-32 border-t border-white/10 bg-[#0D1730]">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid lg:grid-cols-2 gap-14 items-start">
                    <div>
                        <SectionHead
                            eyebrow="Gap Assessment"
                            title="Identify Compliance Gaps Before They Become Problems"
                            sub="Our compliance review examines every statutory touchpoint of your business."
                        />
                        <Reveal delay={0.1}>
                            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="gap-review-list">
                                {GAP_REVIEW_ITEMS.map((item) => (
                                    <li key={item} className="flex items-center gap-2.5 text-sm text-slate-300">
                                        <span className="w-1 h-1 rounded-full bg-sky-400 shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <a
                                href="#contact"
                                data-testid="gap-review-cta"
                                className="mt-9 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
                            >
                                Request a Compliance Review <ArrowRight size={16} />
                            </a>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <div className="border border-white/10 bg-[#0A1128] p-8 sm:p-10" data-testid="gap-quiz">
                            <p className="eyebrow">60-Second Compliance Health Check</p>
                            <AnimatePresence mode="wait">
                                {!done ? (
                                    <motion.div
                                        key={step}
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -24 }}
                                        transition={{ duration: 0.35 }}
                                    >
                                        <div className="mt-6 flex items-center gap-2">
                                            {GAP_QUESTIONS.map((_, i) => (
                                                <span key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-sky-400" : "bg-white/10"}`} />
                                            ))}
                                        </div>
                                        <p className="mt-3 font-mono2 text-[10px] tracking-[0.2em] uppercase text-slate-500">
                                            Question {step + 1} of {GAP_QUESTIONS.length}
                                        </p>
                                        <h3 className="mt-4 font-heading text-lg font-semibold text-white leading-snug">
                                            {GAP_QUESTIONS[step].q}
                                        </h3>
                                        <div className="mt-6 space-y-3">
                                            {GAP_QUESTIONS[step].options.map((opt, oi) => (
                                                <button
                                                    key={opt.label}
                                                    data-testid={`gap-option-${step}-${oi}`}
                                                    onClick={() => answer(opt.score)}
                                                    className="w-full text-left px-5 py-3.5 border border-white/15 rounded-lg text-sm text-slate-200 hover:border-sky-400/60 hover:bg-sky-400/5 transition-colors duration-200"
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="result"
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.4 }}
                                        className={`mt-6 border ${result.border} ${result.bg} p-8 text-center`}
                                        data-testid="gap-result"
                                    >
                                        <ResultIcon size={44} className={`${result.color} mx-auto`} />
                                        <h3 className={`mt-4 font-heading text-2xl font-bold ${result.color}`}>{result.title}</h3>
                                        <p className="mt-3 text-sm text-slate-300 leading-relaxed">{result.msg}</p>
                                        <div className="mt-7 flex flex-wrap justify-center gap-3">
                                            <a
                                                href="#contact"
                                                data-testid="gap-result-cta"
                                                className="px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
                                            >
                                                Request a Compliance Review
                                            </a>
                                            <button
                                                data-testid="gap-restart-btn"
                                                onClick={reset}
                                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-sm text-slate-300 hover:text-white transition-colors"
                                            >
                                                <RotateCcw size={14} /> Retake
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default GapAssessment;
